/**
 * @file 牌面绘制模块（M4-T2；M4-T3 对齐美术规格 08）。
 *
 * 在没有美术资产的前提下，用 PixiJS v8 的 Graphics + Text **程序化近似**地
 * 绘制 108 张牌的全部 27 种牌面 + 牌背，并在初始化时烘焙成纹理缓存。
 * 之后所有牌精灵共享同一张纹理 → 走批处理，draw call 可控（ADR-001 意图）。
 *
 * 牌面配色以美术规格 `design/gdd/08-ui-visual-spec.md` §5/§6 为**唯一基准**：
 * - 万（wan）→ 红 `#b3362c`（汉字数字 + 萬）
 * - 筒（tong）→ 蓝 `#1f5fa8`（汉字数字 + 筒）
 * - 条（tiao）→ 绿 `#178a52`（汉字数字 + 条）
 * - 牌面底色：米白 `#f7f3e8`，描边暖灰 `#c9c2b2`（`--bg-tile-face` / `--border-tile`）
 * - 牌背底色：深绿 `#174a35`，描边 `#2b6b4e`（`--bg-tile-back` / `--border-tile-back`），
 *   中央菱形纹样：填充 `#1e5c40`、描边 `#3d8a63`（§6.5）
 *
 * 2026-08-12 用户拍板：三种花色统一为「汉字数字 + 花色字」样式（颜色区分），
 * 放弃早期圆点/竹节图形方案（辨识度不足）。
 *
 * 绘制基准纹理为 60×84，各档位（手牌 46×64 / 弃牌 30×40 / 他家 26×40 /
 * 副露 40×56 / 34×46）由调用方用统一 `scale` 缩放（§6.1「同一绘制函数 + scale
 * 复用」）。字号/点位按手牌档规格换算回 60×84 基准（乘 `HAND_SCALE` 倒数）。
 *
 * ## 信息隔离
 * 本模块只消费 `TileKind`（牌面种类字符串），不接触任何对局状态。
 * 一张牌「该不该露正面」由调用方（tableScene）决定，本模块只管画。
 */

import {
  Application,
  Container,
  Graphics,
  Sprite,
  Text,
  Texture,
} from 'pixi.js';
import type { Rank, Suit, TileKind } from '@majiang/core';

/** 牌面（正面）的基础绘制尺寸。显示时用 sprite.scale 缩放。 */
export const TILE_W = 60;
export const TILE_H = 84;
const TILE_R = 8;

/** 花色配色（美术规格 08 §5 Token 表：万红 / 筒蓝 / 条绿）。导出供桌面组件（定缺花色卡等）复用。 */
export const SUIT_COLOR: Record<Suit, number> = {
  wan: 0xb3362c,
  tong: 0x1f5fa8,
  tiao: 0x178a52,
};

/** 点数 → 中文数字。 */
const CN_NUM: readonly string[] = ['一', '二', '三', '四', '五', '六', '七', '八', '九'];

/** 花色顺序（与 core 的 SUITS 一致）。 */
const SUIT_ORDER: readonly Suit[] = ['wan', 'tong', 'tiao'];

/** 字体栈：中文字形回退链，与 React 外壳一致。 */
const FONT_STACK = 'system-ui, -apple-system, "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif';

/**
 * ## 牌面方案（2026-08-12 用户拍板：筒/条与万统一为「数字 + 字」）
 *
 * 三种花色全部用「汉字数字 + 花色字」绘制，仅颜色区分（万红/筒蓝/条绿）：
 * - 万（wan）→ 红 #b3362c：大号汉字数字 + 右下角「萬」
 * - 筒（tong）→ 蓝 #1f5fa8：大号汉字数字 + 右下角「筒」
 * - 条（tiao）→ 绿 #178a52：大号汉字数字 + 右下角「条」
 *
 * 放弃圆点（筒）/竹节（条）图形方案 —— 用户反馈辨识度不足。
 * 字号规格同万：数字 39（手牌档）→ 烘焙 60×84 基准；字 16。
 */

/* ─────────────────────────── 纹理缓存 ─────────────────────────── */

interface TileArtCache {
  readonly textures: Map<string, Texture>;
  readonly back: Texture;
}

let cache: TileArtCache | null = null;

/** 烘焙单张牌面到纹理。 */
function bakeFace(app: Application, draw: (g: Container) => void): Texture {
  const holder = new Container();
  draw(holder);
  const tex = app.renderer.generateTexture({
    target: holder,
    resolution: 1,
    antialias: true,
  });
  holder.destroy({ children: true });
  // 不放进全局 Assets 缓存，避免跨房间残留
  return tex;
}

/* ─────────────────────────── 各牌面画法 ─────────────────────────── */

/** 牌面底座（米白圆角矩形 + 描边）。§6.1：`--bg-tile-face` / `--border-tile`。 */
function drawBase(g: Container): void {
  const base = new Graphics();
  base.roundRect(1, 1, TILE_W - 2, TILE_H - 2, TILE_R);
  base.fill(0xf7f3e8);
  base.stroke({ width: 2, color: 0xc9c2b2 });
  g.addChild(base);
}

/**
 * 统一「汉字数字 + 花色字」牌面（万红/筒蓝/条绿，2026-08-12 用户拍板）。
 *
 * 布局：大号汉字数字（居中偏上）+ 花色字（右下角，与万牌风格一致）。
 * 字号规格：数字 39 / 字 16（手牌档）→ 60×84 烘焙基准。
 */
function drawNumFace(g: Container, rank: Rank, color: number, glyph: string): void {
  const num = new Text({
    text: CN_NUM[rank - 1] ?? '',
    style: { fontSize: 39, fill: color, fontFamily: FONT_STACK, fontWeight: '700' },
  });
  num.anchor.set(0.5);
  num.position.set(TILE_W / 2, TILE_H / 2 - 4);
  g.addChild(num);

  const suitChar = new Text({
    text: glyph,
    style: { fontSize: 16, fill: color, fontFamily: FONT_STACK, fontWeight: '700' },
  });
  suitChar.anchor.set(0.5);
  suitChar.position.set(TILE_W - 14, TILE_H - 14);
  g.addChild(suitChar);
}

/** 各花色角标字（万/筒/条，2026-08-12 统一「数字+字」方案）。 */
const SUIT_GLYPH: Record<Suit, string> = {
  wan: '萬',
  tong: '筒',
  tiao: '条',
};

/** 由牌面种类绘制正面容器：三种花色统一「汉字数字 + 花色字」，仅颜色不同。 */
function drawFace(g: Container, kind: TileKind): void {
  drawBase(g);
  const suit = kind.slice(0, -1) as Suit;
  const rank = Number(kind.slice(-1)) as Rank;
  drawNumFace(g, rank, SUIT_COLOR[suit], SUIT_GLYPH[suit]);
}

/** 牌背：深绿底 + 中央菱形纹样。§6.5：`--bg-tile-back` / `--border-tile-back`，菱形 `#1e5c40`/`#3d8a63`。 */
function drawBack(g: Container): void {
  const base = new Graphics();
  base.roundRect(1, 1, TILE_W - 2, TILE_H - 2, TILE_R);
  base.fill(0x174a35);
  base.stroke({ width: 2, color: 0x2b6b4e });
  g.addChild(base);

  // 中央菱形内框（Graphics.polygon）：手牌档对角线约 20×16 → 烘焙约 26×21
  const diamond = new Graphics();
  const cx = TILE_W / 2;
  const cy = TILE_H / 2;
  const dx = 13;
  const dy = 10.5;
  diamond.poly([
    cx,
    cy - dy,
    cx + dx,
    cy,
    cx,
    cy + dy,
    cx - dx,
    cy,
  ]);
  diamond.fill(0x1e5c40);
  diamond.stroke({ width: 1.5, color: 0x3d8a63 });
  g.addChild(diamond);
}

/* ─────────────────────────── 公开 API ─────────────────────────── */

/**
 * 初始化牌面纹理缓存。必须在 `app.init()` 之后调用一次。
 *
 * ⚠ 每次调用都会重新烘焙（28 张，代价可忽略）：纹理归属当前 renderer，
 * 若旧 renderer 已 destroy（React StrictMode 双挂载 / 换房重建），旧纹理即失效，
 * 必须重新生成，不能用「已初始化」短路。
 */
export async function initTileArt(app: Application): Promise<void> {
  const textures = new Map<string, Texture>();
  for (const suit of SUIT_ORDER) {
    for (let r = 1 as Rank; r <= 9; r = (r + 1) as Rank) {
      const kind = `${suit}${r}` as TileKind;
      textures.set(kind, bakeFace(app, (g) => drawFace(g, kind)));
    }
  }
  const back = bakeFace(app, drawBack);
  cache = { textures, back };
}

/** 取某牌面纹理。须先调用 {@link initTileArt}。 */
export function tileTexture(kind: TileKind): Texture {
  if (cache === null) throw new Error('[tileArt] 纹理缓存未初始化：先调 initTileArt(app)');
  const tex = cache.textures.get(kind);
  if (tex === undefined) throw new Error(`[tileArt] 未知牌面: ${kind}`);
  return tex;
}

/** 取牌背纹理。须先调用 {@link initTileArt}。 */
export function backTexture(): Texture {
  if (cache === null) throw new Error('[tileArt] 纹理缓存未初始化：先调 initTileArt(app)');
  return cache.back;
}

/**
 * 牌精灵工厂：正面牌。`scale` 默认 1（即 TILE_W×TILE_H 原始尺寸）。
 * 返回的 Sprite 共享同一纹理，可自由改 position/scale/rotation。
 */
export function tileSprite(kind: TileKind, scale = 1): Sprite {
  const s = new Sprite({ texture: tileTexture(kind) });
  s.scale.set(scale);
  return s;
}

/** 牌背精灵工厂。 */
export function backSprite(scale = 1): Sprite {
  const s = new Sprite({ texture: backTexture() });
  s.scale.set(scale);
  return s;
}

/** 测试/自检用：当前缓存是否就绪。 */
export function isTileArtReady(): boolean {
  return cache !== null;
}
